import React, { useState, useEffect, useContext, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";
import { SelectedExerciseItem } from "./SelectedExerciseItem";
import { AvailableExerciseItem } from "./AvailableExerciseItem";

export const MainContent = ({
  selectedMainId,
  selectedSubId,
  onSelectedExercisesChange,
}) => {
  const { darkMode } = useContext(ThemeContext);
  const [exerciseIDList, setExerciseIDList] = useState([]);
  const { token } = useAuthContext();
  const [allExercises, setAllExercises] = useState([]);
  const [selectedExercises, setSelectedExercises] = useState([]);

  const renderingExercises = useMemo(() => {
    return allExercises.map((exercise) => {
      const selectedExercise = selectedExercises.find(
        (exer) => exer.exerciseid === exercise.id
      );
      return selectedExercise
        ? {
            ...exercise,
            instructions: selectedExercise.instructions,
            hold: selectedExercise.hold,
            repeat: selectedExercise.repeat,
            timesperday: selectedExercise.timesperday,
            range: selectedExercise.range,
            resistance: selectedExercise.resistance,
            direction: selectedExercise.direction,
            select: true,
          }
        : { ...exercise, select: false };
    });
  }, [allExercises, selectedExercises]);

  useEffect(() => {
    onSelectedExercisesChange(selectedExercises);
  }, [selectedExercises, onSelectedExercisesChange]);

  useEffect(() => {
    if (selectedMainId && selectedSubId) {
      const fetchExerciseIDs = async () => {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/condition/getexercises`,
            {
              area: selectedMainId,
              acondition: selectedSubId,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const dataArray = response.data.split(",").map(Number);
          setExerciseIDList(dataArray.slice(1)); // Avoid setting empty or incorrect data
        } catch (err) {
          console.log("Error fetching exercise IDs:", err);
        }
      };
      fetchExerciseIDs();
    }
  }, [selectedMainId, selectedSubId, token]);

  useEffect(() => {
    if (exerciseIDList.length > 0) {
      const fetchExercises = async () => {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/exercise/getexercises`,
            {
              ids: exerciseIDList,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setAllExercises(response.data);
        } catch (err) {
          console.log("Error fetching exercises:", err);
        }
      };
      fetchExercises();
    }
  }, [exerciseIDList, token]); // Fet

  const handleAddToSelected = (id) => {
    const newSelected = [
      ...selectedExercises,
      {
        ...allExercises.find((exerc) => exerc.id === id),
        exerciseid: id,
      },
    ];
    setSelectedExercises(newSelected);
  };

  const handleDeselect = (id) => {
    setSelectedExercises(
      selectedExercises.filter((exerc) => exerc.exerciseid !== id)
    );
  };

  return (
    <Box sx={{ pl: 4, flexGrow: 1 }}>
      {selectedMainId && selectedSubId && (
        <Box sx={{ mb: 8 }}>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 600,
              color: darkMode
                ? getColors.drawerTextDark
                : getColors.thumbnailTextLight,
              textAlign: "left",
              mb: 3,
            }}
          >
            {" "}
            Selected Exercises{" "}
          </Typography>
          <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={4}>
            {renderingExercises
              .filter((exercise) => exercise.select)
              .map((exercise, index) => (
                <SelectedExerciseItem
                  key={index}
                  {...exercise}
                  onRemove={handleDeselect}
                  onChange={(value) => {
                    const clone = [...selectedExercises];
                    const exerciseIdx = clone.findIndex(
                      (exer) => exer.exerciseid === value.id
                    );
                    clone[exerciseIdx] = {
                      ...value,
                      exerciseid: value.id,
                    };
                    setSelectedExercises(clone);
                  }}
                />
              ))}
          </Box>
          <Box sx={{ mt: 8, mb: 8 }}>
            <Typography
              sx={{
                fontSize: "22px",
                fontWeight: 600,
                color: darkMode
                  ? getColors.drawerTextDark
                  : getColors.thumbnailTextLight,
                textAlign: "left",
                mb: 3,
              }}
            >
              {" "}
              Available Exercises{" "}
            </Typography>
            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={4}>
              {renderingExercises
                .filter((exercise) => !exercise.select)
                .map((exercise, index) => (
                  <AvailableExerciseItem
                    key={index}
                    {...exercise}
                    onAdd={handleAddToSelected}
                  />
                ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
